import { initialSelectedParticipant } from "Context";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import CustomSendingLoader from "global/components/CustomSendingLoader/CustomSendingLoader";
import strings from "global/constants/StringConstants";
import {
  openSuccessNotification,
  openErrorNotification,
  isTruthy,
  handleErrorMessage,
  getCallbackKeyFromUrl,
} from "helpers/methods";
import { useAppSelector } from "hooks/use-app-select";
import {
  FileField,
  RequestFile,
  RequestParticipant,
  Reviewers,
} from "models/interfaces";
import { useReducer, useState, useEffect, useMemo } from "react";
import {
  selectScreen,
  setLoading,
  setReduxSelectedParticipant,
  setReduxSignRequest,
} from "redux/appSlice";
import { useAppDispatch } from "redux/hooks";
import { initialState } from "screens/Shared/CommonHelper";
import {
  areAllFieldsFilled,
  downloadCombinedFileAsZip,
  fetchPdfSignRequest,
  fieldsRemaining,
  getSignedFile,
  getUnsignedFile,
  reducer,
  SignerInitialState,
  validateAndProcessPdfFiles,
} from "./ParticipantScreenService";
import {
  approveRejectSignRequest,
  delegateRequest,
  fetchRoleFromToken,
  getDocumentUrl,
  signPdf,
} from "../SignRequestService";
import history from "utils/history";
import ParticipantsPrompts from "./ParticipantsPrompts";
import ParticipantMobileScreen from "./ParticipantMobileScreen";
import ParticipantDesktopScreen from "./ParticipantDesktopScreen";
import ShowRequestStatus from "./components/ShowRequestStatus";
import { RootState } from "utils/store";
import urls from "global/constants/UrlConstants";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { AppModal } from "global/components/AppModal";
import {
  reasonContent,
  reasonFooter,
  reasonHeader,
  REASON_MAX_LENGTH,
} from "./ParticipantScreenHelper";
import InPersonSessionShell from "screens/InPersonSigning/InPersonSessionShell";
import { selectInPersonSession } from "redux/inPersonSessionSlice";
import { globalEmitter } from "utils/emitter";
import { needsSignerIdv } from "helpers/identityVerificationHelpers";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { IS_EMBED } from "embed/embedMode";
import {
  embedErrorCode,
  postEmbedError,
  postEmbedEventOnce,
} from "embed/embedBridge";
import { useEmbedTheme } from "embed/useEmbedTheme";
import { buildEmbedTheme, validHex } from "embed/embedTheme";

const ParticipantScreen = () => {
  useTitle(title.SIGN_REQUEST);
  const urlParams = window.location.search;
  const reqToken = new URLSearchParams(urlParams).get("id");
  const isSignerLoggedIn = new URLSearchParams(urlParams).get("signer");
  const { loading } = useAppSelector((state: RootState) => state.app);
  const isMobile = useAppSelector(selectScreen);
  const accountName = useAppSelector((state) => state.auth.account);
  const [state, dispatch] = useReducer(reducer, SignerInitialState);
  const appDispatch = useAppDispatch();
  const { selectedParticipant } = useAppSelector(
    (state: RootState) => state.app,
  );
  // In-person hand-off mode: the host is driving the session on their device.
  // Require the URL flag, an active host session, AND that the session was
  // started for THIS token — so a remote signer can't append `?inperson=1` to
  // their own link to skip the login prompt, and a stale session left behind by
  // an earlier envelope can't drive the shell (wrong signer name, and a
  // "Return to host" that would mutate a different envelope's session).
  const inPersonSession = useAppSelector(selectInPersonSession);
  const inPerson =
    new URLSearchParams(urlParams).get("inperson") === "1" &&
    inPersonSession.active &&
    Boolean(reqToken) &&
    inPersonSession.requestToken === reqToken;
  const [selectedFile, setSelectedFile] = useState<any>({
    unsignedPdfUrl: "",
    unsignedPdf: "",
    signedPdf: "",
    fields: [],
    pageCount: 0,
    pageUrls: {},
    dimensions: {},
  });
  const [currentReviewer, setCurrentReviewer] = useState<RequestParticipant>(
    initialSelectedParticipant,
  );
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [leftFields, setLeftFields] = useState<number[]>([]);
  const [allFieldsFilled, setAllFeildsFilled] = useState<boolean>(false);
  const [allRequiredFieldsFilled, setAllRequiredFeildsFilled] =
    useState<boolean>(false);
  const [signingJourneyOn, setSigningJourneyOn] = useState<boolean>(false);
  const [currentFieldOnFocus, setCurrentFieldOnFocus] = useState<number>(1);
  const [fileSrc, setFileSrc] = useState<string>("");
  const [readyToSign, setReadyToSign] = useState<boolean>(false);
  // Sealing the PDF and re-fetching the signed copy runs long enough to warrant
  // the explanatory overlay rather than the bare `loading` spinner. Scoped to
  // the actual sign call, so field-validation bail-outs never trigger it.
  const [isApplyingSignature, setIsApplyingSignature] =
    useState<boolean>(false);
  const [requestIsVoided, setReqIsVoided] = useState({
    status: false,
    voiderMsg: "",
  });
  const [signerLoginPropmt, setSignerLoginPrompt] = useState<boolean>(false);
  const [allFieldsFilledPopup, setAllFeildsFilledPopup] =
    useState<boolean>(false);
  const [attachSingleCertificate, setAttachSingleCertificate] =
    useState<boolean>(false);
  const [docLoadSuccess, setDocLoadSuccess] = useState(false);
  const [docLoading, setDocLoading] = useState<boolean>(false);
  const [openReason, setOpenReason] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [reasonError, setReasonError] = useState<string>("");
  const [delegationEmail, setDelegationEmail] = useState<string>("");
  const [delegationEmailError, setDelegationEmailError] = useState<string>("");
  const [delegationName, setDelegationName] = useState<string>("");
  const [delegationNameError, setDelegationNameError] = useState<string>("");
  const [dialogType, setDialogType] = useState<string>("");
  const [allowDelegation, setAllowDelegation] = useState<boolean>(
    state.signRequest?.signRequestConfig?.allowSignatureDelegation!,
  );
  // Set when any signer document call answers `403 IDV_REQUIRED:` (announced
  // centrally by handleErrorMessage), in case this copy of the request didn't
  // say verification was needed — e.g. it was required after the page loaded.
  const [idvRequiredByError, setIdvRequiredByError] = useState<boolean>(false);
  const isIdvPending =
    needsSignerIdv(state.signRequest?.signerIdentityVerification) ||
    idvRequiredByError;

  useEffect(() => {
    const routeToIdentityVerification = () => {
      setIdvRequiredByError(true);
      setReadyToSign(false);
    };
    globalEmitter.on(strings.IDV_REQUIRED_EVENT, routeToIdentityVerification);
    return () => {
      globalEmitter.off(strings.IDV_REQUIRED_EVENT, routeToIdentityVerification);
    };
  }, []);
  // An embedded signer's session belongs to the partner app, so it is never forwarded (server enforces too).
  const delegationAllowed = allowDelegation && !IS_EMBED;

  // Embedded signer (desktop and mobile, dialogs included): apply the colours through one MUI theme --
  // the parent page's override first, then the tenant branding, else the app's own theme (not wrapped).
  const muiTheme = useTheme();
  const embedTheme = useEmbedTheme();
  const tenantBranding: any = (state.signRequest as any)?.branding;
  const embedPrimary =
    validHex(embedTheme.primaryColor) || validHex(tenantBranding?.primaryColor);
  const embedButtonText =
    validHex(embedTheme.buttonTextColor) ||
    validHex(tenantBranding?.buttonTextColor);
  const embedSignerTheme = useMemo(
    () =>
      IS_EMBED && embedPrimary
        ? buildEmbedTheme(muiTheme, embedPrimary, embedButtonText)
        : null,
    [muiTheme, embedPrimary, embedButtonText],
  );

  useEffect(() => {
    const fetchData = async () => {
      await fetchPdfSignRequest(
        reqToken!,
        selectedFile,
        setSelectedFile,
        setLoaders,
        setFileSrc,
        dispatch,
        setReqIsVoided,
        fetchPdfRequester,
        errorHandler,
        setAttachSingleCertificate,
        setAllowDelegation,
      );
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fieldIds = areAllFieldsFilled(
      state.signRequest,
      setAllFeildsFilled,
      setAllRequiredFeildsFilled,
    );

    setLeftFields(fieldIds);
  }, [state]);

  // Push derived status to Redux so UI can read from Redux-only signRequest
  useEffect(() => {
    const derivedStatus =
      state.signRequest.status || state.signRequest.signers?.[0]?.status || "";
    if (derivedStatus) {
      appDispatch(setReduxSignRequest({ status: derivedStatus }));
    }
  }, [state.signRequest.status, state.signRequest.signers]);

  useEffect(() => {
    const requestStatus = state.signRequest.status;
    const currentSigner = state.signRequest.signers?.find(
      (s) => s.email === selectedParticipant.email,
    );
    const currentSignerStatus = currentSigner?.status;
    const isDelegated = currentSigner?.delegated === true;
    const sec =
      selectedParticipant.requestSecurity ??
      (currentSigner as RequestParticipant)?.requestSecurity;
    const needVerification =
      !!sec?.needSecuredAccess &&
      (sec?.isVerified === false || sec?.verified === false);

    const currentReviewer = state.signRequest.approval?.reviewers?.find(
      (r: any) => r.email === selectedParticipant.email,
    );
    const currentReviewerStatus = currentReviewer?.status;
    if (
      needVerification ||
      isIdvPending ||
      requestStatus === strings.declined ||
      requestStatus === strings.voided ||
      requestStatus === strings.expired ||
      requestStatus === strings.signed ||
      currentSignerStatus === strings.declined ||
      currentSignerStatus === strings.signed ||
      currentReviewerStatus?.toLowerCase() ===
        strings.declined?.toLowerCase() ||
      currentReviewerStatus?.toLowerCase() === "rejected" ||
      currentReviewerStatus?.toLowerCase() === strings.approved.toLowerCase() ||
      isDelegated ||
      errorMessage
        .toLocaleLowerCase()
        .includes(strings.requestDelegatedMessage.toLocaleLowerCase())
    ) {
      setReadyToSign(false);
    }
  }, [
    state.signRequest.status,
    state.signRequest.signers,
    state.signRequest.approval?.reviewers,
    selectedParticipant.email,
    selectedParticipant.requestSecurity,
    errorMessage,
    isIdvPending,
  ]);

  useEffect(() => {
    if (state.signRequest.signers[0]?.status === strings.pending) {
      setAllFeildsFilledPopup(allRequiredFieldsFilled);
    }
  }, [allFieldsFilled, allRequiredFieldsFilled]);

  // Embedded: the signer's document loaded (once per page load). The server's own "opened" audit row is
  // written by the fetch itself.
  useEffect(() => {
    if (
      IS_EMBED &&
      state.signRequest.files?.length &&
      state.signRequest.status !== strings.inReview &&
      state.signRequest.signers?.[0]?.status === strings.pending
    ) {
      postEmbedEventOnce("signer_viewed", { signerRole: "Signer" });
    }
  }, [state.signRequest.files, state.signRequest.signers, state.signRequest.status]);

  // Embedded approver: the document loaded for them while the envelope waits for its approvers.
  useEffect(() => {
    if (
      IS_EMBED &&
      state.signRequest.files?.length &&
      state.signRequest.status === strings.inReview
    ) {
      postEmbedEventOnce("approval_viewed", { signerRole: "Approver" });
    }
  }, [state.signRequest.files, state.signRequest.status]);

  // Keep local selectedFile in sync with reducer state (fields are now immutable updates)
  useEffect(() => {
    if (!state.signRequest.files?.length) return;
    // Try to find the currently selected file in the updated list
    const next =
      state.signRequest.files.find(
        (f: RequestFile) =>
          f.unsignedPdf === selectedFile?.unsignedPdf ||
          f.unsignedDoc === selectedFile?.unsignedDoc,
      ) || state.signRequest.files[0];

    if (!next) return;

    const currentFileId =
      selectedFile?.unsignedPdf || selectedFile?.unsignedDoc;
    const nextFileId = next.unsignedPdf || next.unsignedDoc;

    if (currentFileId !== nextFileId) {
      // Different file selected - update selectedFile completely
      setSelectedFile(next);
    } else if (selectedFile) {
      // Same file - only update fields and other properties, preserve URL references
      // This prevents PDF reload while keeping field data in sync
      setSelectedFile({
        ...selectedFile,
        fields: next.fields,
        // Preserve URL references to prevent Document component from reloading
        unsignedPdfUrl: selectedFile.unsignedPdfUrl || next.unsignedPdfUrl,
        unsignedDocUrl: selectedFile.unsignedDocUrl || next.unsignedDocUrl,
      });
    }
  }, [state.signRequest.files]);

  const setLoaders = (value: boolean) => {
    appDispatch(setLoading(value));
    setDocLoading(value);
  };

  const fetchPdfRequester = async (reviewers?: Reviewers[]) => {
    try {
      const response: RequestParticipant = await fetchRoleFromToken(reqToken!);
      // Force `requestSecurity` to be an own key on the dispatched payload
      // (even when the backend response omits it) so the reducer's merge
      // always overwrites any stale requestSecurity left in redux/localStorage
      // from a previously opened request, rather than leaving it in place.
      appDispatch(
        setReduxSelectedParticipant({
          ...response,
          requestSecurity: response.requestSecurity,
        }),
      );
      if (isTruthy(reviewers) && reviewers) {
        const currReviewer = reviewers.find(
          (reviewer: Reviewers) =>
            reviewer.email === response.email &&
            response.requestRole === "Reviewer",
        );
        if (currReviewer) setCurrentReviewer(currReviewer);
      }
    } catch (error: any) {
      if (error.message.includes(strings.alreadyApprovedMessage)) {
        setReqIsVoided({ status: true, voiderMsg: error.message });
      } else {
        handleErrorMessage(error);
      }
    }
  };

  const errorHandler = (error: string) => {
    setErrorMessage(error);
    if (IS_EMBED) {
      postEmbedError(embedErrorCode({ message: error, statusCode: 400 }));
    }
    const normalizedError = error.toLocaleLowerCase();
    const isNoLongerAssociatedError = normalizedError.includes(
      strings.nonAssociatedErrorMessage.toLocaleLowerCase(),
    );
    const status = error
      .toLocaleLowerCase()
      .includes(strings.voided.toLocaleLowerCase())
      ? strings.voided
      : error.toLocaleLowerCase().includes(strings.expired.toLocaleLowerCase())
        ? strings.expired
        : error
              .toLocaleLowerCase()
              .includes(strings.declined.toLocaleLowerCase())
          ? strings.declined
          : error
                .toLocaleLowerCase()
                .includes(strings.requestDelegatedMessage.toLocaleLowerCase())
            ? strings.requestDelegatedMessage
            : isNoLongerAssociatedError
              ? strings.nonAssociatedErrorMessage
              : error
                    .toLocaleLowerCase()
                    .includes(
                      strings.notYourTurnErrorMessageV1.toLocaleLowerCase(),
                    )
                ? strings.notYourTurnErrorMessageV1
                : error
                      .toLocaleLowerCase()
                      .includes(
                        strings.notYourTurnErrorMessageV2.toLocaleLowerCase(),
                      )
                  ? strings.notYourTurnErrorMessageV2
                  : "";

    dispatch({
      type: "SET_PDF_SIGN_REQUEST",
      signRequest: { ...state.signRequest, status },
    });

    // Switch to ShowRequestStatus if request is declined, voided, expired, or delegated
    if (
      status === strings.declined ||
      status === strings.voided ||
      status === strings.expired ||
      error
        .toLocaleLowerCase()
        .includes(strings.requestDelegatedMessage.toLocaleLowerCase())
    ) {
      setReadyToSign(false);
    }

    return status;
  };

  const handleFileChange = async (value: string) => {
    const newSelectedFile = state.signRequest.files.find(
      (file: RequestFile) => file.unsignedPdf === value || file.unsignedDoc === value,
    );
    if (!newSelectedFile) return;

    if (state.signRequest.signers?.[0]?.status === strings.signed) {
      getSignedFile(
        newSelectedFile,
        reqToken!,
        selectedFile,
        setDocLoading,
        setFileSrc,
        setSelectedFile,
      );
    } else {
      try {
        setDocLoading(true);
        const response = await getDocumentUrl(newSelectedFile, true, reqToken!, true);
        const fileWithUrl = { ...newSelectedFile, unsignedPdfUrl: response.message };
        setSelectedFile(fileWithUrl);
        setDocLoading(false);
        return fileWithUrl;
      } catch (error: any) {
        setDocLoading(false);
        handleErrorMessage(error);
      }
    }
  };

  const handleApproveRejectRequest = async (isApprove: boolean) => {
    try {
      // Use Redux loading only — setLoaders also toggles docLoading and stacks a second spinner on the viewer.
      setLoader(true);
      await approveRejectSignRequest(isApprove, reqToken!);
      if (IS_EMBED) {
        // An embedded approver's decision, as soon as the server accepted it (see signer events).
        postEmbedEventOnce(isApprove ? "approval_completed" : "approval_rejected", {
          signerRole: "Approver",
        });
      }
      openSuccessNotification(
        isApprove ? "Sign request approved!" : "Sign request rejected!",
      );
      await fetchPdfSignRequest(
        reqToken!,
        selectedFile,
        setSelectedFile,
        setLoader,
        setFileSrc,
        dispatch,
        setReqIsVoided,
        fetchPdfRequester,
        errorHandler,
        setAttachSingleCertificate,
        setAllowDelegation,
      );
    } catch (error: any) {
      handleErrorMessage(error);
      setLoader(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoader(true);
      setAllFeildsFilledPopup(false);
      if (allRequiredFieldsFilled) {
        let filesToSend = structuredClone(state.signRequest.files);
        const { allFieldsValid, invalidField, invalidReason } =
          validateAndProcessPdfFiles(filesToSend);
        if (allFieldsValid) {
          setIsApplyingSignature(true);
          await signPdf(reqToken!, filesToSend);
          if (IS_EMBED) {
            // Signed is a fact as soon as the server accepted it: do not hold the partner's event back
            // behind the re-fetch below, which can fail on its own (network) after a successful signature.
            postEmbedEventOnce("sign_completed", {
              signerRole: "Signer",
              completedAt: new Date().toISOString(),
            });
          }
          await fetchPdfSignRequest(
            reqToken!,
            selectedFile,
            setSelectedFile,
            setLoader,
            setFileSrc,
            dispatch,
            setReqIsVoided,
            fetchPdfRequester,
          );
          openSuccessNotification("Successfully signed document!");
          if (IS_EMBED) {
            // The partner app owns what happens next (it was told above): stay on the signed view, and do
            // not navigate or offer an Inkfree login inside its iframe.
            return;
          }
          // Keep the in-person flag on the URL so the hand-off shell detects the
          // finish and offers the next signer / summary instead of the remote
          // signer-login prompt. Also keep `key=` -- any /callback/ call made
          // after this push (e.g. re-fetching the now-signed document) reads it
          // straight off the URL, and losing it here would 401 that call.
          const callbackKey = encodeURIComponent(getCallbackKeyFromUrl());
          history.push(
            inPerson
              ? `${urls.doSign}?id=${reqToken}&key=${callbackKey}&inperson=1&signed=true`
              : `${urls.doSign}?id=${reqToken}&key=${callbackKey}&signer=true&signed=true`,
          );
          if (!inPerson && !isSignerLoggedIn && accountName !== "esigners") {
            setSignerLoginPrompt(true);
          }
        } else {
          if (invalidField) {
            moveToNextFieldLocation(invalidField?.id);
            if (invalidReason?.includes("max length")) {
              openErrorNotification(
                `The value in "${invalidField.type}" is longer than allowed (${invalidField.maxLength || 200} characters). Please shorten it and try again.`,
              );
            } else if (invalidReason) {
              openErrorNotification(invalidReason);
            }
          }
        }
      } else {
        moveToNextFieldLocation(leftFields[0]);
      }
    } catch (error: any) {
      // Backend re-checks the verification lock at sign time. If the signer
      // is locked, signPdf throws HTTP 429. Mark the signer locked in Redux
      // and drop back to ShowRequestStatus, which now picks the lockout card.
      const code = error?.status ?? error?.statusCode ?? error?.code;
      if (code === 429) {
        appDispatch(
          setReduxSelectedParticipant({
            requestSecurity: {
              ...(selectedParticipant as any)?.requestSecurity,
              failedAttempts: strings.verificationMaxAttempts,
              isVerified: false,
            },
          }),
        );
        setReadyToSign(false);
        return;
      }
      if (
        !(
          error.message.includes(strings.nonAssociatedErrorMessage) ||
          error.message.includes(strings.notYourTurnErrorMessageV1) ||
          error.message.includes(strings.notYourTurnErrorMessageV2)
        )
      ) {
        handleErrorMessage(error);
      }
    } finally {
      setIsApplyingSignature(false);
      setLoader(false);
    }
  };

  const downloadFile = (file?: any) => {
    let newfile = document.createElement("a");
    //@ts-ignore
    newfile.style = "display:none";
    newfile.href = file ? file : fileSrc;
    newfile.download = `${selectedFile?.signedPdf
      .split("_")
      .slice(1)
      .join("_")}`;
    newfile.click();
    URL.revokeObjectURL(newfile.href);
  };

  const moveToNextFieldLocation = async (
    fieldId: number,
    signJourney?: boolean,
  ) => {
    let fileOfNextField: string = "";
    let nextField: FileField | undefined = undefined;

    const lastFieldId = fieldsRemaining(state.signRequest.files).totalFields;

    if (fieldId <= lastFieldId) {
      for (let file of state.signRequest.files) {
        const nextFieldFound = file.fields.find(
          (eachField: FileField) => eachField.id === fieldId,
        );
        if (nextFieldFound) {
          if (signJourney === true) {
            const skipInSigningJourney =
              isTruthy(nextFieldFound.value) ||
              nextFieldFound.type === "date_signed" ||
              nextFieldFound.type === strings.email ||
              !nextFieldFound.required ||
              nextFieldFound.type === "checkbox";
            if (skipInSigningJourney) {
              await moveToNextFieldLocation(nextFieldFound.id + 1, true);
              return;
            } else {
              fileOfNextField = file.unsignedPdf || file.unsignedDoc;
              nextField = nextFieldFound;
              break;
            }
          } else {
            fileOfNextField = file.unsignedPdf || file.unsignedDoc;
            nextField = nextFieldFound;
            break;
          }
        }
      }
    }
    if (nextField && isTruthy(fileOfNextField)) {
      await goToNextField(nextField, fileOfNextField);
      setCurrentFieldOnFocus(nextField.id);
    }
  };

  const goToNextField = async (
    nextField: FileField,
    fileOfNextField: string,
  ) => {
    let currentFile = selectedFile;
    try {
      if (fileOfNextField !== selectedFile.unsignedPdf) {
        const changedFile = await handleFileChange(fileOfNextField);
        if (!changedFile) return;
        currentFile = changedFile || selectedFile;
        setSelectedFile(changedFile);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      const scrollToField = (currentFile: RequestFile) => {
        try {
          const container = document.getElementById("pdf_box");
          const $page = document.querySelector<HTMLElement>(
            `.react-pdf__Page[data-page-number="${nextField.page + 1}"]`,
          );

          if (container && $page) {
            const { height } = $page.getBoundingClientRect();
            const pageY =
              (nextField.y / currentFile?.dimensions[nextField.page].y) *
                height +
              nextField.page * height;

            const pageRect = $page.getBoundingClientRect();
            const pageHeight = pageRect.height;
            const fieldYPosition =
              ((nextField.existingField
                ? (pageY / height) * 100
                : nextField.fieldPageY!) *
                pageHeight) /
              100;

            const targetScrollTop =
              container.scrollTop +
              (pageRect.top - container.getBoundingClientRect().top) +
              fieldYPosition -
              container.clientHeight / 3;
            container.scrollTo({
              top: targetScrollTop,
              behavior: "smooth",
            });
          } else {
            setTimeout(scrollToField, 300);
          }
        } catch (error: any) {
          handleErrorMessage(error);
        }
      };

      scrollToField(currentFile);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const setLoader = (value: boolean) => {
    appDispatch(setLoading(value));
  };

  const downloadDocs = (file?: any) => {
    let newfile = document.createElement("a");
    //@ts-ignore
    newfile.style = "display:none";
    newfile.href = file ? file : fileSrc;
    newfile.download = `${selectedFile?.signedPdf
      .split("_")
      .slice(1)
      .join("_")}`;
    newfile.click();
    URL.revokeObjectURL(newfile.href);
  };

  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const downloadDocsAsZip = () => {
    downloadCombinedFileAsZip(
      reqToken!,
      state.signRequest,
      setIsDownloadingZip,
    );
  };

  const getOnClickFunction = async () => {
    try {
      switch (dialogType) {
        case strings.decline:
          history.push({
            pathname: "/decline/request",
            search: `?id=${reqToken}&key=${encodeURIComponent(getCallbackKeyFromUrl())}&signer=true`,
            state: {
              pdfFiles: state.signRequest?.files || [],
              selectedFile: selectedFile || null,
            },
          });
          setOpenReason(false);
          break;
        case strings.delegate:
          const emailTrimmed = delegationEmail.trim();
          const nameTrimmed = delegationName.trim();
          const isSelfDelegation =
            emailTrimmed.toLowerCase() ===
            selectedParticipant.email.toLowerCase();

          if (!emailTrimmed) {
            setDelegationEmailError("Delegation email is required");
            appDispatch(setLoading(false));
            return;
          }

          // Disallow comma-separated emails.
          if (emailTrimmed.includes(",")) {
            setDelegationEmailError("Please enter a single valid email");
            appDispatch(setLoading(false));
            return;
          }

          if (!strings.regex.test(emailTrimmed)) {
            setDelegationEmailError("Please enter a valid email");
            appDispatch(setLoading(false));
            return;
          }

          if (!nameTrimmed) {
            setDelegationNameError("Delegation name is required");
            appDispatch(setLoading(false));
            return;
          }

          if (nameTrimmed.length > 255) {
            setDelegationNameError(`Signer name cannot exceed 255 characters `);
            appDispatch(setLoading(false));
            return;
          }

          if (emailTrimmed.length > 255) {
            setDelegationEmailError(
              `Signer email cannot exceed 255 characters`,
            );
            appDispatch(setLoading(false));
            return;
          }

          if (reason.length > REASON_MAX_LENGTH) {
            setReasonError(
              `Private message cannot exceed ${REASON_MAX_LENGTH} characters`,
            );
            appDispatch(setLoading(false));
            return;
          }

          if (isSelfDelegation) {
            setDelegationEmailError("You cannot delegate to yourself");
            appDispatch(setLoading(false));
            return;
          }
          appDispatch(setLoading(true));
          await delegateRequest(
            reqToken!,
            delegationEmail,
            delegationName,
            reason,
          );
          openSuccessNotification("Request delegated successfully!");
          setDialogType("");
          setDelegationEmail("");
          setDelegationEmailError("");
          setOpenReason(false);
          await fetchPdfSignRequest(
            reqToken!,
            selectedFile,
            setSelectedFile,
            setLoaders,
            setFileSrc,
            dispatch,
            setReqIsVoided,
            fetchPdfRequester,
            errorHandler,
            setAttachSingleCertificate,
          );
          setReadyToSign(false);
          break;
        default:
          break;
      }
    } catch (error: any) {
      const errorMessage = error?.message || "";
      const status = errorMessage ? errorHandler(errorMessage) : "";
      if (!status) {
        handleErrorMessage(error);
      }
      appDispatch(setLoading(false));
    } finally {
      appDispatch(setLoading(false));
    }
  };

  const delegationEmailTrimmed = delegationEmail.trim();
  const delegationNameTrimmed = delegationName.trim();
  const isDelegationEmailInvalid =
    !delegationEmailTrimmed ||
    delegationEmailTrimmed.includes(",") ||
    !strings.regex.test(delegationEmailTrimmed) ||
    delegationEmailTrimmed.toLowerCase() ===
      (selectedParticipant.email || "").toLowerCase();
  const isDelegationSubmitDisabled =
    dialogType === strings.delegate &&
    (isDelegationEmailInvalid || !delegationNameTrimmed);

  const screenContent = (
    <>
      {/* During the initial (pre-signing) view, ShowRequestStatus renders its
          own branded loading screen, so suppress the global white overlay to
          avoid washing it out with a second spinner. */}
      <CustomLoader
        isLoading={loading && readyToSign && !isApplyingSignature}
      />
      <CustomSendingLoader
        isLoading={isApplyingSignature}
        variant="signature"
        title={strings.APPLYING_SIGNATURE_TITLE}
        description={strings.APPLYING_SIGNATURE_DESCRIPTION}
      />
      {!readyToSign ? (
        <ShowRequestStatus
          signRequest={state.signRequest}
          reqToken={reqToken!}
          errorMessage={errorMessage}
          startSigning={() => {
            // The document stays closed until identity verification passes.
            if (!isIdvPending) setReadyToSign(true);
          }}
          identityVerificationRequired={idvRequiredByError}
          onIdentityVerified={async () => {
            // Re-read the request (now with fields and document access). Like
            // the access-code step, this lands on the usual start card
            // (Preview / Sign) rather than opening the document directly.
            await fetchPdfSignRequest(
              reqToken!,
              selectedFile,
              setSelectedFile,
              setLoaders,
              setFileSrc,
              dispatch,
              setReqIsVoided,
              fetchPdfRequester,
              errorHandler,
              setAttachSingleCertificate,
              setAllowDelegation,
            );
            setIdvRequiredByError(false);
          }}
          loading={loading}
          setLoading={setLoader}
          downloadDocs={() => downloadDocsAsZip()}
          isDownloadingZip={isDownloadingZip}
          setDialogType={setDialogType}
          setReasonOpen={setOpenReason}
          setDelegationEmail={setDelegationEmail}
          allowDelegation={delegationAllowed}
          handleApproveRejectRequest={handleApproveRejectRequest}
          onVerificationSuccess={async () => {
            const response = await fetchRoleFromToken(reqToken!);
            appDispatch(
              setReduxSelectedParticipant({
                ...response,
                requestSecurity: response.requestSecurity,
              }),
            );
            await fetchPdfSignRequest(
              reqToken!,
              selectedFile,
              setSelectedFile,
              setLoaders,
              setFileSrc,
              dispatch,
              setReqIsVoided,
              fetchPdfRequester,
              errorHandler,
              setAttachSingleCertificate,
              setAllowDelegation,
            );
          }}
        />
      ) : (
        <>
          <ParticipantsPrompts
            state={state}
            openSignerLoginPopup={signerLoginPropmt}
            setSignerLoginPopup={setSignerLoginPrompt}
            allFieldsFilledPopup={allFieldsFilledPopup}
            setAllFeildsFilledPopup={setAllFeildsFilledPopup}
            handleFinish={handleSubmit}
            loading={loading}
          />
          {isMobile ? (
            <ParticipantMobileScreen
              state={state}
              dispatch={dispatch}
              setLoading={setLoader}
              selectedFile={selectedFile!}
              setSelectedFile={setSelectedFile}
              fileSrc={fileSrc}
              currentPageNumber={currentPageNumber}
              setCurrentPageNumber={setCurrentPageNumber}
              handleFileChange={handleFileChange}
              handleSubmit={handleSubmit}
              downloadFile={downloadFile}
              currentReviewer={currentReviewer}
              handleApproveRejectRequest={handleApproveRejectRequest}
              reqToken={reqToken!}
              currentFieldOnFocus={currentFieldOnFocus}
              setCurrentFieldOnFocus={setCurrentFieldOnFocus}
              moveToFieldLocation={moveToNextFieldLocation}
              signingJourneyOn={signingJourneyOn}
              setSigningJourneyOn={setSigningJourneyOn}
              docLoading={docLoading}
              setDocLoading={setDocLoading}
              docLoadSuccess={docLoadSuccess}
              setDocLoadSuccess={setDocLoadSuccess}
              loading={loading}
              allFieldsFilled={allFieldsFilled}
              allRequiredFieldsFilled={allRequiredFieldsFilled}
              attachSingleCertificate={attachSingleCertificate}
              downloadDocs={downloadDocs}
              downloadDocsAsZip={downloadDocsAsZip}
              isDownloadingZip={isDownloadingZip}
              setDialogType={setDialogType}
              setReasonOpen={setOpenReason}
              setDelegationEmail={setDelegationEmail}
              allowDelegation={delegationAllowed}
            />
          ) : (
            <ParticipantDesktopScreen
              state={state}
              dispatch={dispatch}
              loading={loading}
              setLoading={setLoader}
              selectedFile={selectedFile!}
              setSelectedFile={setSelectedFile}
              fileSrc={fileSrc}
              currentPageNumber={currentPageNumber}
              setCurrentPageNumber={setCurrentPageNumber}
              handleFileChange={handleFileChange}
              handleSubmit={handleSubmit}
              downloadFile={downloadFile}
              currentReviewer={currentReviewer}
              handleApproveRejectRequest={handleApproveRejectRequest}
              reqToken={reqToken!}
              currentFieldOnFocus={currentFieldOnFocus}
              setCurrentFieldOnFocus={setCurrentFieldOnFocus}
              moveToFieldLocation={moveToNextFieldLocation}
              signingJourneyOn={signingJourneyOn}
              setSigningJourneyOn={setSigningJourneyOn}
              attachSingleCertificate={attachSingleCertificate}
              docLoading={docLoading}
              setDocLoading={setDocLoading}
              docLoadSuccess={docLoadSuccess}
              setDocLoadSuccess={setDocLoadSuccess}
              allFieldsFilled={allFieldsFilled}
              allRequiredFieldsFilled={allRequiredFieldsFilled}
              downloadDocs={downloadDocs}
              downloadDocsAsZip={downloadDocsAsZip}
              isDownloadingZip={isDownloadingZip}
              setDialogType={setDialogType}
              setReasonOpen={setOpenReason}
              setDelegationEmail={setDelegationEmail}
              allowDelegation={delegationAllowed}
            />
          )}
        </>
      )}
      <AppModal
        open={openReason}
        onClose={() => setOpenReason(false)}
        title={reasonHeader(dialogType)}
        tone={dialogType === strings.decline ? "destructive" : "neutral"}
        size="md"
        footer={reasonFooter(
          dialogType,
          (value: boolean) => setOpenReason(value),
          getOnClickFunction,
          setDelegationEmail,
          setDelegationName,
          setReason,
          setReasonError,
          setDelegationEmailError,
          setDelegationNameError,
          isDelegationSubmitDisabled,
        )}
      >
        {reasonContent(
          dialogType,
          reason,
          setReason,
          reasonError,
          setReasonError,
          delegationEmail,
          setDelegationEmail,
          delegationEmailError,
          setDelegationEmailError,
          delegationName,
          setDelegationName,
          delegationNameError,
          setDelegationNameError,
        )}
      </AppModal>
    </>
  );

  // Outside an embed (or with no colour to apply) there is no wrapper, so nothing changes there.
  const themedContent = embedSignerTheme ? (
    <ThemeProvider theme={embedSignerTheme}>{screenContent}</ThemeProvider>
  ) : (
    screenContent
  );

  return inPerson ? (
    <InPersonSessionShell>{themedContent}</InPersonSessionShell>
  ) : (
    themedContent
  );
};
export default ParticipantScreen;
